import React from 'react'
import type { BusyButtonProps } from '@ztechapps/webui.button'
import { BusyButton } from '@ztechapps/webui.button'
import { useInject, WebuiIocKey, type TaskListRowActionMenuProps } from '@ztechapps/webui.contract'
import { RowActionMenuButtonLink } from '@ztechapps/webui.data-tools'
import { useTaskActionsContext } from './Context'
import { useCloneProjectTask } from '@ztechapps/project.common'
import { useError, useHistory } from '@ztechapps/common.hook'
import type { RouteDef } from '@ztechapps/common.contract'

const cloneIcon = 'clone'
const title = 'Clone this task.'

const useCloneTask = () => {
  const { taskId, projectId } = useTaskActionsContext()

  const [cloneTask, { loading, error }] = useCloneProjectTask()
  useError(error)

  const onClone = async (args?: { taskId: string; projectId: string }) => {
    return cloneTask({
      projectId: args?.projectId ?? projectId,
      sourceTaskId: args?.taskId ?? taskId,
    })
  }

  return {
    onClone,
    loading,
  }
}

export const CloneProjectTask = (props: BusyButtonProps) => {
  const history = useHistory()
  const taskRoute = useInject<RouteDef>(WebuiIocKey.routes.taskItem)

  const { onClone, loading } = useCloneTask()

  const onClick = async () => {
    const { task } = await onClone()

    const taskId = task?._id

    history.push(taskRoute.altPath({ _id: taskId }))
  }

  return (
    <BusyButton
      {...props}
      variant="primary"
      text="Clone"
      isBusy={loading}
      icon={cloneIcon}
      title={title}
      onClick={onClick}
    />
  )
}

export const CloneProjectTaskRowActionMenu = ({ task, as, taskListContext }: TaskListRowActionMenuProps) => {
  const { disabled, setLoading } = taskListContext
  const taskId = task?._id
  const projectId = task?.project?._id

  const { onClone } = useCloneTask()

  const As = as || RowActionMenuButtonLink

  const onClick = async () => {
    try {
      setLoading(true)
      await onClone({ taskId, projectId })
    } finally {
      setLoading?.(false)
    }
  }

  return <As icon={cloneIcon} label={title} disabled={disabled} onClick={onClick} />
}
